import {GfaLocaleServiceV1} from '@volkswagen-onehub/gfa-locale-service';
import {DataVersionServiceV1} from '@volkswagen-onehub/data-version-service';
import {
  BffConfigurationResponseMap,
  BffOptionsParameters,
  getFeatureAppConfigurationEndpoint,
  MockIds,
  mockIdsParam,
  parseMockIds,
} from '@volkswagen-onehub/vw-e-mobility-service-responses';
import {SimulatorFeatureAppName} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

import {encodeBffConfigParameters} from './encode-bff-config-parameters';
import {ServiceConfigEndpointProvider} from '@volkswagen-onehub/service-config-endpoint-provider/lib/v1';

export function readMockIds(urlParams: URLSearchParams): Set<string> {
  const parsed = parseMockIds(urlParams.get(mockIdsParam));

  // special handling for "isCommercial" flag for backwards compatibility
  if (urlParams.get('isCommercial') === 'true') {
    parsed.add(MockIds.commercial);
  }

  return parsed;
}

/**
 * For debugging and testing, we can upload a custom ACS or I18N config, if one
 * is specified in the local storage. This changes the endpoint and method.
 * (POST to /custom-configuration instead of GET to /configuration)
 */
export function getCustomConfigsFromLocalStorage(
  configId: string
): string | undefined {
  const acs = localStorage.getItem(`${configId}-acs`);
  const i18n = localStorage.getItem(`${configId}-i18n`);
  const contentConfig = localStorage.getItem(
    `${configId}-content-configuration`
  );

  if (!acs && !i18n && !contentConfig) {
    return undefined;
  }

  return JSON.stringify({
    ...(acs ? {acs: JSON.parse(acs)} : null),
    ...(i18n ? {i18n: JSON.parse(i18n)} : null),
    ...(contentConfig ? {contentConfig: JSON.parse(contentConfig)} : null),
  });
}

export async function fetchBffConfig<T extends SimulatorFeatureAppName>(
  featureAppName: T,
  featureServices: {
    localeService: GfaLocaleServiceV1;
    dataVersion?: DataVersionServiceV1;
  },
  serviceConfigEndpointProvider: ServiceConfigEndpointProvider,
  envBaseUrl?: string,
  options?: BffOptionsParameters
): Promise<BffConfigurationResponseMap[T]> {
  const {language, countryCode} = featureServices.localeService;

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const mockIds = readMockIds(urlParams);

  const debugParamForBff = urlParams.get('debug') || undefined;

  /**
   * Workaround to allow cache busting from within the CMS.
   * We use the hash/version of the current CMS service config, so TQA/ROM can
   * circumvent the cache by changing/republishing the service config.
   * This mirrors the MOFA implementation, but isn't that pretty.
   *
   * (One issue is that TQA won't allow any url parameters or similar for this,
   * as it then "could be different from the production behaviour", oh well)
   */
  const cacheBuster = featureServices.dataVersion?.serviceConfigsVersion;

  const searchString = encodeBffConfigParameters({
    language,
    countryCode,
    featureAppName,
    serviceConfigEndpointProvider,
    mockIds,
    cacheBuster,
    debug: debugParamForBff,
    options,
  });

  const customConfigBody = getCustomConfigsFromLocalStorage(featureAppName);

  // If we have any custom configs we use the POST endpoint
  const endpoint = getFeatureAppConfigurationEndpoint(customConfigBody);

  // This is a human-configured value, get rid of ending slash if it was provided.
  const baseUrl = (envBaseUrl ?? '').replace(/\/$/, '');

  const requestUrl = `${baseUrl}${endpoint}?${searchString}`;

  const rawResponse = await fetch(
    requestUrl,
    customConfigBody ? {method: 'POST', body: customConfigBody} : undefined
  );

  if (!rawResponse.ok) {
    throw new Error(`${rawResponse.status} - ${rawResponse.statusText}`);
  }

  return rawResponse.json();
}
